import { z } from 'zod';
import { PointSchema } from './types';
// =============================================================================
// AI CALL CONTEXT (Determinism + Replayability)
// =============================================================================
/**
 * Context for every AI call — enables debugging and replay
 */
export const AICallContextSchema = z.object({
    roundSeed: z.number().int(), // Deterministic RNG seed
    policyVersion: z.string(), // "mob-director-v1.2.0"
    stateSnapshotHash: z.string(), // SHA256 of summarized state
    timestamp: z.date(),
});
// =============================================================================
// ROOM/ZONE GRAPH
// =============================================================================
/**
 * Exit properties for room connections
 */
export const ExitPropertiesSchema = z.object({
    locked: z.boolean().default(false),
    trapped: z.boolean().default(false),
    noisy: z.boolean().default(false), // Alerts mobs when used
    visible: z.boolean().default(true), // Hidden passage if false
    oneWay: z.boolean().default(false),
});
/**
 * Exit connecting two rooms
 */
export const ExitSchema = z.object({
    id: z.string().uuid(),
    fromRoomId: z.string(),
    toRoomId: z.string(),
    direction: z.string(), // "north", "up", "through_grate"
    properties: ExitPropertiesSchema,
});
/**
 * Interactable object in a room
 */
export const InteractableSchema = z.object({
    id: z.string(),
    type: z.string(), // "brazier", "chandelier", "pit"
    position: PointSchema,
    state: z.string().optional(), // "lit", "broken", etc.
});
/**
 * Room node in the world graph
 */
export const RoomSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    zoneId: z.string(),
    terrainFeatures: z.array(z.string()), // "chokepoint", "high_ground"
    ambientState: z.string().optional(), // "torches_lit", "flooded"
    spawnPoints: z.array(PointSchema).default([]),
    maxMobs: z.number().int().positive().optional(),
    interactables: z.array(InteractableSchema).default([]),
});
/**
 * Patrol route within a zone
 */
export const PatrolRouteSchema = z.object({
    id: z.string(),
    roomIds: z.array(z.string()), // Ordered list of room IDs
    mobIds: z.array(z.string()), // Mobs assigned to this route
    currentRoomIndex: z.number().int().default(0),
    pauseDurationMs: z.number().int().default(30000), // Time at each room
});
/**
 * Zone subgraph with faction ownership
 */
export const ZoneSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    roomIds: z.array(z.string()),
    factionId: z.string().nullable(),
    alertLevel: z.number().min(0).max(100).default(0), // Decays over WorldTicks
    heatLevel: z.number().min(0).max(100).default(0), // Recent player activity
    patrolRoutes: z.array(PatrolRouteSchema).default([]),
});
// =============================================================================
// WORLD TICK & TIME DILATOR
// =============================================================================
/**
 * Time speed presets for DM control
 */
export const TimeSpeedSchema = z.enum([
    'PAUSE', // Stopped - need to think, look up rules
    'SLOW', // 1/4x - tense negotiation, exploration
    'NORMAL', // 1x - standard play
    'FAST', // 2x - quick trash mob combat
    'MAX', // Testing, speedrun mode
]);
/**
 * Time multipliers for each speed preset
 */
export const TIME_MULTIPLIERS = {
    PAUSE: 0,
    SLOW: 0.25,
    NORMAL: 1,
    FAST: 2,
    MAX: 10,
};
/**
 * Time dilator state for DM control
 */
export const TimeDilatorSchema = z.object({
    speed: TimeSpeedSchema.default('NORMAL'),
    worldTickBaseMs: z.number().int().min(1000).max(60000).default(10000),
    combatRoundBaseMs: z.number().int().min(1000).max(30000).default(6000),
    pausedAt: z.date().nullable().default(null),
    skipToTime: z.date().nullable().default(null), // "Skip to dawn" feature
});
/**
 * Get effective tick interval based on dilator state
 */
export function getEffectiveTickMs(dilator, type) {
    const multiplier = TIME_MULTIPLIERS[dilator.speed];
    if (multiplier === 0)
        return Infinity; // Paused
    const baseMs = type === 'world' ? dilator.worldTickBaseMs : dilator.combatRoundBaseMs;
    return Math.max(1000, baseMs / multiplier);
}
// =============================================================================
// COMBAT TEMPO (Fine-grained combat pacing)
// =============================================================================
/**
 * Combat tempo presets - how "intense" the combat feels
 */
export const CombatTempoSchema = z.enum([
    'CINEMATIC', // Slow, dramatic - long pauses, detailed narration
    'DELIBERATE', // Thoughtful - time to plan, discuss tactics
    'NORMAL', // Standard 5e pacing
    'URGENT', // Faster decisions, shorter narration
    'FRENETIC', // Rapid-fire, chaos of battle
]);
/**
 * Tempo effects on combat mechanics
 */
export const TEMPO_EFFECTS = {
    CINEMATIC: {
        turnTimerSec: null,
        aiDeliberationMs: 3000,
        narratorVerbosity: 'FULL',
        barkFrequency: 1.0,
        aiAggressionMod: -0.1,
        dramaticPausesEnabled: true,
    },
    DELIBERATE: {
        turnTimerSec: 120,
        aiDeliberationMs: 2000,
        narratorVerbosity: 'FULL',
        barkFrequency: 0.8,
        aiAggressionMod: 0,
        dramaticPausesEnabled: true,
    },
    NORMAL: {
        turnTimerSec: 60,
        aiDeliberationMs: 1500,
        narratorVerbosity: 'BRIEF',
        barkFrequency: 0.5,
        aiAggressionMod: 0,
        dramaticPausesEnabled: false,
    },
    URGENT: {
        turnTimerSec: 30,
        aiDeliberationMs: 800,
        narratorVerbosity: 'BRIEF',
        barkFrequency: 0.3,
        aiAggressionMod: 0.1,
        dramaticPausesEnabled: false,
    },
    FRENETIC: {
        turnTimerSec: 15,
        aiDeliberationMs: 400,
        narratorVerbosity: 'MINIMAL',
        barkFrequency: 0.2,
        aiAggressionMod: 0.2,
        dramaticPausesEnabled: false,
    },
};
/**
 * Ramp mode - how tempo changes over combat
 */
export const TempoRampModeSchema = z.enum([
    'FIXED', // Stay at set tempo
    'ESCALATING', // Start slow, speed up each round
    'DE_ESCALATING', // Start fast, slow for dramatic finish
    'WAVE', // Alternate fast/slow for rhythm
    'ADAPTIVE', // AI adjusts based on combat state
]);
/**
 * Combat tempo state - DM's fine-grained control
 */
export const CombatTempoStateSchema = z.object({
    // Current tempo
    currentTempo: CombatTempoSchema.default('NORMAL'),
    // Ramp settings
    rampMode: TempoRampModeSchema.default('FIXED'),
    rampStartTempo: CombatTempoSchema.optional(), // For ESCALATING/DE_ESCALATING
    rampEndTempo: CombatTempoSchema.optional(),
    rampOverRounds: z.number().int().min(1).max(20).optional(), // How many rounds to ramp
    // Manual overrides
    turnTimerOverrideSec: z.number().int().positive().nullable().default(null),
    pauseOnCrits: z.boolean().default(true),
    pauseOnKills: z.boolean().default(true),
    pauseOnSpells: z.boolean().default(false), // Pause for big spells
    // Current state
    currentRound: z.number().int().min(0).default(0),
    lastTempoChange: z.date().nullable().default(null),
    // Keyboard shortcut state
    quickTempoLocked: z.boolean().default(false), // Prevent accidental changes
});
/**
 * Calculate effective tempo based on ramp mode and current round
 */
export function getEffectiveTempo(state) {
    if (state.rampMode === 'FIXED' || !state.rampStartTempo || !state.rampEndTempo) {
        return state.currentTempo;
    }
    const tempoOrder = ['CINEMATIC', 'DELIBERATE', 'NORMAL', 'URGENT', 'FRENETIC'];
    const startIdx = tempoOrder.indexOf(state.rampStartTempo);
    const endIdx = tempoOrder.indexOf(state.rampEndTempo);
    const rounds = state.rampOverRounds || 5;
    if (state.rampMode === 'ESCALATING') {
        const progress = Math.min(1, state.currentRound / rounds);
        const idx = Math.round(startIdx + (endIdx - startIdx) * progress);
        return tempoOrder[Math.max(0, Math.min(tempoOrder.length - 1, idx))];
    }
    if (state.rampMode === 'DE_ESCALATING') {
        const progress = Math.min(1, state.currentRound / rounds);
        const idx = Math.round(endIdx + (startIdx - endIdx) * progress);
        return tempoOrder[Math.max(0, Math.min(tempoOrder.length - 1, idx))];
    }
    if (state.rampMode === 'WAVE') {
        // Alternate every 2 rounds
        const isHighTempo = Math.floor(state.currentRound / 2) % 2 === 0;
        return isHighTempo ? state.rampEndTempo : state.rampStartTempo;
    }
    // ADAPTIVE - would use combat state (not implemented yet)
    return state.currentTempo;
}
/**
 * Get tempo effects with any overrides applied
 */
export function getTempoEffects(state) {
    const tempo = getEffectiveTempo(state);
    const base = TEMPO_EFFECTS[tempo];
    return {
        ...base,
        turnTimerSec: state.turnTimerOverrideSec ?? base.turnTimerSec,
        dramaticPausesEnabled: state.pauseOnCrits || state.pauseOnKills || state.pauseOnSpells,
    };
}
/**
 * WorldTick configuration
 */
export const WorldTickConfigSchema = z.object({
    intervalMs: z.number().int().min(5000).max(60000).default(15000),
    enabledSystems: z.array(z.enum([
        'patrols',
        'rumors',
        'factions',
        'heat',
        'reinforcements',
        'npc_goals',
    ])).default(['patrols', 'heat']),
    timeDilator: TimeDilatorSchema.optional(),
});
// =============================================================================
// MOB STATUS (Automated HP/Condition Tracking)
// =============================================================================
/**
 * HP tier for visual display
 */
export const HpTierSchema = z.enum([
    'healthy', // 100-75%
    'wounded', // 74-50%
    'bloody', // 49-25%
    'critical', // 24-1%
    'unconscious', // 0 HP with death saves
    'dead', // 0 HP, no saves
]);
/**
 * 5e Conditions
 */
export const ConditionTypeSchema = z.enum([
    'blinded',
    'charmed',
    'deafened',
    'frightened',
    'grappled',
    'incapacitated',
    'invisible',
    'paralyzed',
    'petrified',
    'poisoned',
    'prone',
    'restrained',
    'stunned',
    'unconscious',
    'exhaustion_1',
    'exhaustion_2',
    'exhaustion_3',
    'exhaustion_4',
    'exhaustion_5',
    'exhaustion_6',
    'concentrating',
    'bloodied',
]);
/**
 * Active condition on a mob/character
 */
export const ActiveConditionSchema = z.object({
    type: ConditionTypeSchema,
    source: z.string().optional(), // "Sage's Hold Person"
    duration: z.number().int().nullable(), // Rounds remaining, null = indefinite
    saveEndOfTurn: z.boolean().default(false),
});
/**
 * Concentration tracking
 */
export const ConcentrationSchema = z.object({
    spell: z.string(),
    targetIds: z.array(z.string()),
    startedRound: z.number().int(),
});
/**
 * Complete mob status
 */
export const MobStatusSchema = z.object({
    mobId: z.string(),
    hpTier: HpTierSchema,
    hpPercent: z.number().min(0).max(100),
    conditions: z.array(ActiveConditionSchema),
    concentratingOn: ConcentrationSchema.nullable(),
});
/**
 * Calculate HP tier from percentage
 */
export function getHpTier(currentHp, maxHp, hasDealthSaves = false) {
    if (currentHp <= 0) {
        return hasDealthSaves ? 'unconscious' : 'dead';
    }
    const percent = (currentHp / maxHp) * 100;
    if (percent >= 75)
        return 'healthy';
    if (percent >= 50)
        return 'wounded';
    if (percent >= 25)
        return 'bloody';
    return 'critical';
}
// =============================================================================
// MEMORY SYSTEM (with Perception)
// =============================================================================
/**
 * Memory scopes determine how long and how far memories persist
 */
export const MemoryScopeSchema = z.enum([
    'ENCOUNTER', // Until combat ends
    'SESSION', // Until long rest
    'FACTION', // Persistent within faction
    'WORLD', // Permanent, known by all
]);
/**
 * Types of facts mobs can remember about players
 */
export const MemoryFactTypeSchema = z.enum([
    'TACTIC', // "Fighter always charges first"
    'DAMAGE_TYPE', // "Wizard uses fire"
    'ROLE', // "Cleric is the healer"
    'KILL', // "Party killed our shaman"
    'MERCY', // "They spared the chief's son"
    'BETRAYAL', // "They broke the parley"
    'NEGOTIATION', // "They paid the toll"
    'THEFT', // "They stole the idol"
]);
/**
 * Source of a memory - affects confidence
 */
export const MemorySourceSchema = z.enum([
    'WITNESSED', // Mob saw it happen (1.0 confidence)
    'REPORTED', // Another mob told them (0.8 confidence)
    'RUMOR', // Faction-level gossip (0.5 confidence)
]);
/**
 * Memory visibility — prevents psychic mobs
 */
export const MemoryVisibilitySchema = z.enum([
    'OBVIOUS', // "Fighter charges every round" — spreads to all in room
    'SUBTLE', // "Cleric healed someone" — only witnesses
    'HIDDEN', // "Rogue is real threat" — requires INT 10+ analysis
]);
/**
 * A single fact that a mob remembers
 */
export const MemoryFactSchema = z.object({
    id: z.string().uuid(),
    type: MemoryFactTypeSchema,
    // What/who the memory is about
    subject: z.union([z.string(), z.literal('PARTY')]),
    detail: z.string(),
    // Provenance
    createdAt: z.date(),
    source: MemorySourceSchema,
    confidence: z.number().min(0).max(1),
    // Perception (prevents psychic mobs)
    perceivedBy: z.array(z.string()), // mob_ids or faction_ids
    visibility: MemoryVisibilitySchema,
    halfLifeMs: z.number().int().positive().nullable(), // Decay for rumors
    // Optional behavior modification
    behaviorMod: z.object({
        action: z.string(),
        weightDelta: z.number(),
    }).optional(),
});
/**
 * A mob's complete memory state
 */
export const MobMemorySchema = z.object({
    mobId: z.string(),
    scope: MemoryScopeSchema,
    facts: z.array(MemoryFactSchema),
    decayPolicy: z.enum(['NEVER', 'ON_REST', 'ON_ZONE_EXIT', 'TIMED']),
    expiresAt: z.date().optional(),
});
// =============================================================================
// ATTENTION SYSTEM (née Threat)
// =============================================================================
/**
 * A single entry in the attention table
 */
export const AttentionEntrySchema = z.object({
    targetId: z.string(),
    attention: z.number(), // Base attention value
    grudge: z.number().default(0), // Memory-based modifier
    tacticalPriority: z.number().default(0), // Protect shaman, hold door, etc.
    lastUpdated: z.date(),
});
/**
 * Complete attention table for a combat
 */
export const AttentionTableSchema = z.object({
    combatId: z.string(),
    entries: z.array(AttentionEntrySchema),
});
/**
 * Attention generation modifiers
 */
export const ATTENTION_MODIFIERS = {
    DAMAGE_PER_POINT: 1.0,
    HEAL_PER_POINT: 0.5,
    CONDITION_APPLIED: 20,
    TAUNT: 50,
    PROXIMITY_MELEE: 1.1,
    // Switching thresholds
    SWITCH_THRESHOLD_MELEE: 1.1, // Need 110% to pull aggro in melee
    SWITCH_THRESHOLD_RANGED: 1.3, // Need 130% to pull aggro at range
    // Grudge (long-term memory) accumulation
    GRUDGE_DAMAGE_RATIO: 0.1, // 10% of damage becomes long-term grudge
};
// Legacy aliases for backward compatibility
export const ThreatEntrySchema = AttentionEntrySchema;
export const ThreatTableSchema = AttentionTableSchema;
export const THREAT_MODIFIERS = ATTENTION_MODIFIERS;
// =============================================================================
// PACK DYNAMICS
// =============================================================================
/**
 * Pack morale states
 */
export const MoraleSchema = z.enum([
    'BOLD', // Outnumber party, aggressive
    'STEADY', // Even fight, tactical
    'WAVERING', // Lost 50%+, defensive
    'BROKEN', // Lost 75%+ or leader dead, flee/surrender
]);
/**
 * Pack formation tactics
 */
export const FormationSchema = z.enum([
    'SURROUND', // Spread to flank, cut off escape
    'FUNNEL', // Drive party toward chokepoint
    'PROTECT', // Squishies behind brutes
    'SCATTERED', // No coordination (broken morale)
]);
/**
 * Pack coordination tactics
 */
export const PackTacticSchema = z.enum([
    'FOCUS_FIRE', // All attack same target
    'STAGGER', // Don't overlap big abilities
    'PEEL', // One mob harasses healer
    'KITE', // Maintain range, use terrain
    'AMBUSH', // Wait for optimal moment
]);
/**
 * Current state of a mob pack
 */
export const PackStateSchema = z.object({
    packId: z.string(),
    mobIds: z.array(z.string()),
    size: z.number().int().positive(),
    originalSize: z.number().int().positive(),
    leaderId: z.string().nullable(),
    leaderAlive: z.boolean(),
    morale: MoraleSchema,
    formation: FormationSchema,
    activeTactics: z.array(PackTacticSchema),
});
// =============================================================================
// MOB INTENT (with Intent → Action separation)
// =============================================================================
/**
 * High-level intent categories
 */
export const MobIntentTypeSchema = z.enum([
    'ELIMINATE_THREAT', // Kill highest threat target
    'DISRUPT_HEALER', // Target healer/support
    'PROTECT_LEADER', // Defend pack leader
    'HOLD_CHOKE', // Control terrain/chokepoint
    'FOCUS_FIRE', // Coordinate on single target
    'CALL_REINFORCEMENTS', // Summon help
    'DEFEND_SELF', // Dodge, disengage, defensive posture
    'ESCAPE', // Flee combat
    'CAPTURE', // Subdue rather than kill
    'PARLEY', // Attempt negotiation
]);
/**
 * Fallback action (pre-validated)
 */
export const FallbackActionSchema = z.object({
    action: z.string(),
    target: z.string().nullable(),
    movement: PointSchema.nullable(),
});
/**
 * A single mob's intended action (with intent + fallbacks)
 */
export const MobIntentSchema = z.object({
    mobId: z.string(),
    // What it's trying to accomplish
    intent: MobIntentTypeSchema,
    // The specific move (may be invalidated)
    proposedAction: z.string(),
    target: z.string().nullable(),
    movement: PointSchema.nullable(),
    // Pre-validated fallbacks, same intent
    fallbackActions: z.array(FallbackActionSchema).default([]),
    reasoning: z.string(),
    bark: z.string().nullable(),
    confidence: z.number().min(0).max(1),
});
// =============================================================================
// MOB BRAIN
// =============================================================================
/**
 * Available mob actions (maps to stat block)
 */
export const MobActionSchema = z.enum([
    'ATTACK',
    'MULTIATTACK',
    'CAST_SPELL',
    'USE_ABILITY',
    'DASH',
    'DISENGAGE',
    'DODGE',
    'HIDE',
    'HELP',
    'READY',
    'SHOVE',
    'GRAPPLE',
]);
/**
 * Weighted behavior for probabilistic selection
 */
export const WeightedBehaviorSchema = z.object({
    action: MobActionSchema,
    weight: z.number().positive(),
    condition: z.string().optional(),
});
/**
 * Hard constraints on mob behavior
 */
export const MobConstraintsSchema = z.object({
    leashRadius: z.number().positive().optional(),
    cannotLeaveRooms: z.array(z.string()).optional(),
    cannotTargetConditions: z.array(z.string()).optional(),
    cannotSpawnLoot: z.boolean().optional(),
    fleeThreshold: z.number().min(0).max(1).optional(),
});
/**
 * Complete mob brain configuration
 */
export const MobBrainSchema = z.object({
    mobId: z.string(),
    mobType: z.string(),
    eligibleForAI: z.boolean().default(true),
    constraints: MobConstraintsSchema,
    memoryScope: MemoryScopeSchema.default('ENCOUNTER'),
    remembers: z.array(z.string()).default([]),
    baseBehaviors: z.array(WeightedBehaviorSchema),
    barks: z.object({
        aggro: z.array(z.string()).optional(),
        lowHp: z.array(z.string()).optional(),
        allyDeath: z.array(z.string()).optional(),
        victory: z.array(z.string()).optional(),
        flee: z.array(z.string()).optional(),
    }).optional(),
});
// =============================================================================
// 5E CONSTRAINTS
// =============================================================================
/**
 * Cover levels
 */
export const CoverLevelSchema = z.enum(['half', 'three_quarters', 'full']);
/**
 * 5e-specific constraints for AI input
 */
export const FiveEConstraintsSchema = z.object({
    allowedActions: z.array(z.string()),
    maxMovement: z.number().int().positive(),
    // Targeting restrictions
    cannotTarget: z.array(z.string()).default([]), // Sanctuary, etc.
    cannotSee: z.array(z.string()).default([]), // Hidden/invisible
    coverTargets: z.record(z.string(), CoverLevelSchema).default({}),
    // Resource tracking
    availableSlots: z.record(z.string(), z.number()).default({}), // {"1": 3, "2": 1}
    reactionAvailable: z.boolean().default(true),
    concentrationActive: z.string().nullable(),
    legendaryActionsRemaining: z.number().int().default(0),
    rechargeAvailable: z.array(z.string()).default([]), // Which recharge abilities ready
    ammunition: z.record(z.string(), z.number()).optional(), // {"arrows": 20}
});
// =============================================================================
// MOB DIRECTOR I/O
// =============================================================================
/**
 * Combat participant info for MobDirector
 */
export const CombatantSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: z.enum(['player', 'npc', 'mob']),
    currentHp: z.number(),
    maxHp: z.number(),
    hpTier: HpTierSchema,
    ac: z.number().optional(),
    position: PointSchema,
    conditions: z.array(ActiveConditionSchema),
    concentratingOn: z.string().nullable(),
    availableActions: z.array(z.string()).optional(),
    recentActions: z.array(z.string()).optional(),
});
/**
 * Room info for tactical decisions
 */
export const CombatRoomSchema = z.object({
    id: z.string(),
    name: z.string().optional(),
    terrainFeatures: z.array(z.string()),
    exits: z.array(z.string()),
    interactables: z.array(InteractableSchema).optional(),
});
/**
 * Input to MobDirector for a combat round
 */
export const MobDirectorInputSchema = z.object({
    combatId: z.string(),
    round: z.number().int().positive(),
    // Determinism context
    context: AICallContextSchema,
    room: CombatRoomSchema,
    party: z.array(CombatantSchema),
    mobs: z.array(CombatantSchema),
    mobBrains: z.array(MobBrainSchema),
    mobMemories: z.array(MobMemorySchema),
    attentionTable: AttentionTableSchema,
    packState: PackStateSchema.optional(),
    constraints: FiveEConstraintsSchema,
});
/**
 * Output from MobDirector
 */
export const MobDirectorOutputSchema = z.object({
    combatId: z.string(),
    round: z.number().int().positive(),
    mobIntents: z.array(MobIntentSchema),
    tacticalNote: z.string().optional(),
    newMemories: z.array(MemoryFactSchema).optional(),
    statusNarrations: z.array(z.string()).optional(),
});
// =============================================================================
// STORY DIRECTOR
// =============================================================================
/**
 * Rumor hook types
 */
export const RumorHookTypeSchema = z.enum([
    'FORESHADOW',
    'QUEST',
    'WARNING',
    'LORE',
    'GUIDANCE',
    'SCHEDULE',
]);
/**
 * A generated rumor
 */
export const RumorCardSchema = z.object({
    id: z.string().uuid(),
    text: z.string().max(240),
    leadsTo: z.object({
        kind: z.enum(['LOCATION', 'NPC', 'ITEM', 'EVENT', 'TIME_HINT']),
        value: z.string(),
    }),
    hookType: RumorHookTypeSchema,
    validUntil: z.date().optional(),
    sourceNpcId: z.string().optional(),
});
// =============================================================================
// AI MODE CONFIGURATION
// =============================================================================
/**
 * AI operation modes
 */
export const AIModeSchema = z.enum([
    'OFF',
    'ASSIST',
    'COPILOT',
    'AUTOPILOT',
]);
/**
 * Table style presets
 */
export const TableStyleSchema = z.enum([
    'GRITTY', // Smart tactics, punishing
    'HEROIC', // Dramatic but survivable
    'COMEDIC', // Silly barks, clumsy mobs
    'FAMILY', // No profanity, toned-down violence
]);
/**
 * Campaign-level AI settings
 */
export const AISettingsSchema = z.object({
    mode: AIModeSchema.default('ASSIST'),
    tableStyle: TableStyleSchema.default('HEROIC'),
    enableMobDirector: z.boolean().default(true),
    enableStoryDirector: z.boolean().default(true),
    enableNarrator: z.boolean().default(true),
    enableMobMemory: z.boolean().default(true),
    enableAutoStatus: z.boolean().default(true),
    profanityLevel: z.enum(['NONE', 'MILD', 'MODERATE']).default('MILD'),
    violenceLevel: z.enum(['MINIMAL', 'STANDARD', 'GRAPHIC']).default('STANDARD'),
    maxCallsPerSession: z.number().int().positive().optional(),
    maxCallsPerCombat: z.number().int().positive().optional(),
    maxLatencyMs: z.number().int().positive().default(3000),
    worldTickConfig: WorldTickConfigSchema.optional(),
});
//# sourceMappingURL=ai-types.js.map